import * as dayjs from 'dayjs';
import cookie from 'js-cookie';
import 'dayjs/locale/ru';
import 'dayjs/locale/uk';
import {
  addLocale,
  useLocale as applyLocale,
  setDefaultLang,
} from 'ttag';
import loadable from '@loadable/component';

export enum Languages {
  uk = 'uk',
  ru = 'ru',
}

export const languages = Object.values(Languages);
export const defaultLang = Languages.uk;
export const languageCookieName = 'language';

export const getRouterBasenameByLang = (lang: Languages) => {
  if (lang === Languages.ru) {
    return undefined;
  }

  return lang;
};

export const getClientLangFromCookie = () => {
  const lang = cookie.get(languageCookieName) as Languages | undefined;

  return lang;
};

export const getServerLangFromCookie = (cookies: Record<string, string>) => {
  const lang = cookies[languageCookieName] as Languages | undefined;

  return lang;
};

export const getLangFromPathname = (path: string) => {
  // @ts-ignore
  const matchInfo = /^\/(?<code>\w+)\//.exec(path);
  const lang = matchInfo?.groups?.code as Languages | undefined;

  return lang;
};

export const getResultLang = (pathname: string, langFromCookie?: Languages) => {
  const langFromPathname = getLangFromPathname(pathname);

  // https://jira.evo/browse/SHAF-8942 open home page on ukraine lang by default
  if (pathname === '/' && !langFromCookie && !langFromPathname) {
    return Languages.uk;
  }

  if (langFromCookie && languages.includes(langFromCookie)) {
    return langFromCookie;
  }

  if (langFromPathname && languages.includes(langFromPathname)) {
    return langFromPathname;
  }

  return Languages.ru;
};

export const getClientLang = () => {
  const { pathname } = window.location;
  const langFromCookie = getClientLangFromCookie();
  const resultLang = getResultLang(pathname, langFromCookie);

  return resultLang;
};

export const getServerLang = (cookies: Record<string, string>, pathname: string) => {
  const langFromCookie = getServerLangFromCookie(cookies);
  const resultLang = getResultLang(pathname, langFromCookie);

  return resultLang;
};

export const getClientLocale = () => {
  const lang = getClientLang();
  const routerBasename = getRouterBasenameByLang(lang);

  return {
    lang,
    routerBasename,
  };
};

export const getServerLocale = (cookies: Record<string, string>, pathname: string) => {
  const lang = getServerLang(cookies, pathname);
  const routerBasename = getRouterBasenameByLang(lang);

  return {
    lang,
    routerBasename,
  };
};

const langPrefixRegexp = new RegExp(`^/(${languages.join('|')})(/|$)`);
// Removes language prefix from path
export const getPathWithoutLang = (path: string) => path.replace(langPrefixRegexp, '/');

type I18nProviderProps = {
  lang: Languages;
  ssr?: boolean,
  children: () => React.ReactElement,
};

export const I18NProviders = {
  uk: null,
  ru: loadable.lib(() => import('@locales/web-ru.po.json')),
};

export const I18NProvider: React.FC<I18nProviderProps> = (props) => {
  const lang = props.lang;

  const Provider = I18NProviders[lang];

  if (!props.ssr) {
    setDefaultLang(defaultLang);
  }

  const handleApplyLocale = () => {
    applyLocale(lang);
    dayjs.locale(lang);
  };

  if (Provider) {
    return (
      <Provider>
        {(translations) => {
          if (translations) {
            if (!props.ssr) {
              addLocale(lang, translations.default || translations);
              handleApplyLocale();
            }
          }

          return props.children();
        }}
      </Provider>
    );
  }

  if (!props.ssr) {
    handleApplyLocale();
  }

  return props.children();
};

export const rupluralize = (value: number, variants: string[]) => {
  const amount = Math.abs(value);

  if (amount % 10 === 1 && amount % 100 !== 11) {
    return variants[0];
  }
  if (amount % 10 >= 2 && amount % 10 <= 4 && (amount % 100 < 10 || amount % 100 >= 20)) {
    return variants[1];
  }
  return variants[2];
};
